/**
 * Chart Export Utilities
 * Export chart data to CSV or PNG
 */

import { format } from 'date-fns';

interface ExportDataPoint {
  timestamp: string;
  [key: string]: any;
}

/**
 * Sanitize a CSV cell to prevent formula injection.
 * Prefixes dangerous leading characters with a single quote.
 */
function sanitizeCsvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (/^[=+\-@|\t\r]/.test(str)) {
    return "'" + str;
  }
  return str;
}

/**
 * Export chart data to CSV
 */
export function exportToCSV(
  data: ExportDataPoint[],
  filename: string,
  headers: Record<string, string>
) {
  // Create CSV content
  const headerRow = Object.values(headers).join(',');
  const dataRows = data.map(row => {
    return Object.keys(headers).map(key => {
      const value = row[key];
      // Handle timestamps
      if (key === 'timestamp') {
        try {
          return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
        } catch {
          return sanitizeCsvCell(value);
        }
      }
      const sanitized = sanitizeCsvCell(value);
      // Handle strings with commas, quotes, or newlines
      if (typeof sanitized === 'string' && /[",\n\r]/.test(sanitized)) {
        return `"${sanitized.replace(/"/g, '""')}"`;
      }
      return sanitized;
    }).join(',');
  });

  const csv = [headerRow, ...dataRows].join('\n');

  // Create download
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${format(new Date(), 'yyyy-MM-dd_HHmm')}.csv`;
  link.click();
  window.URL.revokeObjectURL(url);
}

